import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAcceptInvite } from "../../hooks/league/useAcceptInvite";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useDispatch } from "react-redux";
import { setLeagueId } from "../../features/league/leagueReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faCheckCircle,
  faTimesCircle,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/common/Button";
import Loading from "../../components/common/Loading";

function AcceptInvite() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Try to get params from URL first, then fall back to sessionStorage
  const leagueId =
    queryParams.get("leagueId") || sessionStorage.getItem("leagueId");
  const inviteCode =
    queryParams.get("inviteCode") || sessionStorage.getItem("inviteCode");

  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const { mutate: accept, isLoading, isError, error } = useAcceptInvite();
  const { data: user, isUserLoading, isUserError } = useFetchUser();

  useEffect(() => {
    // Store invite params in session storage when they're present in URL
    if (leagueId && inviteCode) {
      sessionStorage.setItem("leagueId", leagueId);
      sessionStorage.setItem("inviteCode", inviteCode);
    }
  }, [leagueId, inviteCode]);

  const handleAccept = () => {
    if (!isAuthenticated) {
      // If not authenticated, redirect to login and save return URL
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
      return;
    }

    if (user && user._id) {
      accept(
        { leagueId, inviteCode, userId: user._id },
        {
          onSuccess: (data) => {
            // Clear session storage
            sessionStorage.removeItem("leagueId");
            sessionStorage.removeItem("inviteCode");

            // Set the league ID in Redux
            dispatch(setLeagueId(leagueId));

            // Navigate to the league details page instead of home
            navigate(`/league/${leagueId}`, { replace: true });
          },
          onError: (error) => {
            console.error("Error accepting invite:", error.message);
          },
        },
      );
    }
  };

  const handleDecline = () => {
    sessionStorage.removeItem("leagueId");
    sessionStorage.removeItem("inviteCode");
    navigate("/");
  };

  // Only show loading if authenticated and waiting for user data
  if ((isAuthenticated && isUserLoading) || isLoading) return <Loading />;

  // Only show errors for authenticated users
  if (isAuthenticated && (isError || isUserError)) {
    const errorMessage = [
      isError && error.message,
      isUserError && "An error occurred fetching the user.",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-light-grey p-4">
        <div className="card shadow-card max-w-md w-full text-center">
          <FontAwesomeIcon
            icon={faTimesCircle}
            className="text-deep-red text-5xl mb-4"
          />
          <h2 className="text-xl font-bold text-dark-grey mb-4">Error</h2>
          <p className="text-medium-grey mb-6">{errorMessage}</p>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate("/")}
            fullWidth
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-light-grey">
      <div className="w-full max-w-md mb-8 flex justify-center">
        <div className="relative">
          <div className="bg-deep-red text-white rounded-full w-20 h-20 flex items-center justify-center shadow-lg">
            <FontAwesomeIcon icon={faTrophy} className="text-4xl" />
          </div>
          <div className="absolute -bottom-1 -right-1 bg-gold text-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-white shadow-md">
            <FontAwesomeIcon icon={faUserPlus} className="text-sm" />
          </div>
        </div>
      </div>

      <div className="card shadow-card max-w-md w-full">
        <h2 className="text-2xl font-bold text-center text-dark-grey mb-2">
          League Invitation
        </h2>
        <div className="h-1 w-16 bg-deep-red mx-auto mb-6 rounded-full"></div>

        <p className="text-center text-medium-grey mb-8">
          You've been invited to join a Fantasy MMA league. Join now to start
          competing!
        </p>

        <div className="space-y-4">
          <Button
            variant="primary"
            size="lg"
            icon={faCheckCircle}
            onClick={handleAccept}
            fullWidth
            className="shadow-md"
          >
            {isAuthenticated ? "Accept Invitation" : "Log in to Accept"}
          </Button>

          <Button
            variant="outline"
            size="lg"
            icon={faTimesCircle}
            onClick={handleDecline}
            fullWidth
          >
            Decline
          </Button>
        </div>

        <div className="mt-8 text-xs text-center text-medium-grey">
          <p>By accepting, you'll be added to the league roster</p>
        </div>
      </div>

      <div className="mt-8 text-center">
        <img
          src="/emblem-gold.png"
          alt="DayShift Sports"
          className="h-10 mx-auto opacity-70"
        />
      </div>
    </div>
  );
}

export default AcceptInvite;
